// src/pages/relatorios/RelatorioCredenciais.jsx
import React, { useState, useEffect } from 'react';
import { 
  FaFilter, FaFileExcel, FaSearch, FaCalendarAlt,
  FaIdCard, FaUsers, FaBuilding, FaTools,
  FaSpinner, FaInfoCircle
} from 'react-icons/fa';
import { useAuth } from '../../hooks/useAuth';
import supabase from '../../supabaseClient';
import * as XLSX from 'xlsx';

const RelatorioCredenciais = () => {
  const { hasRole, user, userProfile } = useAuth();
  
  // Estados
  const [credenciais, setCredenciais] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [expositorLogado, setExpositorLogado] = useState(null);
  const [expositores, setExpositores] = useState([]);
  const [tiposCredencial, setTiposCredencial] = useState([]);
  
  // Estados para filtros
  const [filters, setFilters] = useState({
    dataInicio: '',
    dataFim: '',
    expositor: '',
    nomeExpositor: '',
    nomeFantasia: '',
    nomeColaborador: '',
    cpf: '',
    tipoCredencial: ''
  });
  
  // Estados para estatísticas
  const [stats, setStats] = useState({
    totalCredenciais: 0,
    totalExpositores: 0,
    totalColaboradores: 0,
    totalTiposCredencial: 0
  });

  // Buscar expositor logado baseado no email
  const fetchExpositorLogado = async () => {
    if (!user?.email || hasRole('admin')) {
      return null; // Admin vê todas as credenciais
    }

    try {
      const { data, error } = await supabase
        .from('caex_expositores')
        .select('*')
        .eq('email', user.email)
        .eq('ativo', true)
        .single();
      
      if (error && error.code !== 'PGRST116') {
        throw error;
      }
      
      return data;
    } catch (error) {
      console.error('Erro ao buscar expositor logado:', error);
      return null;
    }
  };

  // Carregar expositores para o filtro (apenas para admin)
  const fetchExpositores = async () => {
    if (!hasRole('admin')) return;

    try {
      const { data, error } = await supabase
        .from('caex_expositores')
        .select('id, nome_empresa, nome_fantasia, cnpj, email')
        .eq('ativo', true)
        .order('nome_empresa');
      
      if (error) throw error;
      setExpositores(data || []);
    } catch (error) {
      console.error('Erro ao buscar expositores:', error);
    }
  };

  // Carregar tipos de credencial para o filtro
  const fetchTiposCredencial = async () => {
    try {
      // Buscando tipos únicos a partir da tabela caex_credenciais
      const { data, error } = await supabase
        .from('caex_credenciais')
        .select('tipo_credencial')
        .not('tipo_credencial', 'is', null)
        .eq('status', 'ativo')
        .order('tipo_credencial');
      
      if (error) throw error;
      
      // Criando um array de objetos com tipos únicos
      const tiposUnicos = [...new Set(data.map(item => item.tipo_credencial))]
        .filter(Boolean)
        .map((tipo, id) => ({ id, nome: tipo }));
      
      setTiposCredencial(tiposUnicos || []);
    } catch (error) {
      console.error('Erro ao buscar tipos de credencial:', error);
    }
  };

  // Carregar dados das credenciais com filtros aplicados
  const fetchCredenciais = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Buscando credenciais com filtros:', filters);
      
      // Primeiro buscar expositor se for expositor logado
      const expositor = await fetchExpositorLogado();
      setExpositorLogado(expositor);
      
      // Query base para buscar credenciais
      let query = supabase
        .from('caex_credenciais')
        .select(`
          id,
          created_at,
          nome,
          cpf,
          tipo_credencial,
          empresa,
          cargo,
          status,
          expositor_id,
          evento_id,
          observacoes,
          data_inicio,
          data_fim,
          caex_expositores (
            id,
            nome_empresa,
            nome_fantasia,
            cnpj,
            email
          ),
          caex_eventos (
            id,
            nome,
            data_inicio,
            data_fim
          )
        `)
        .eq('status', 'ativo')
        .order('created_at', { ascending: false });

      // Aplicar filtro por expositor se não for admin
      if (!hasRole('admin') && expositor) {
        query = query.eq('expositor_id', expositor.id);
      }

      // Aplicar filtros de data
      if (filters.dataInicio) {
        query = query.gte('created_at', `${filters.dataInicio}T00:00:00`);
      }

      if (filters.dataFim) {
        query = query.lte('created_at', `${filters.dataFim}T23:59:59`);
      }
      
      // Filtro por ID do expositor (para admin)
      if (filters.expositor && hasRole('admin')) {
        query = query.eq('expositor_id', filters.expositor);
      }

      // Filtro por tipo de credencial
      if (filters.tipoCredencial) {
        query = query.eq('tipo_credencial', filters.tipoCredencial);
      }

      // Filtro por CPF
      if (filters.cpf) {
        query = query.ilike('cpf', `%${filters.cpf}%`);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) {
        console.error('Erro ao buscar credenciais:', fetchError);
        throw fetchError;
      }

      console.log('Credenciais carregadas:', data);

      // Verificar se o expositor foi encontrado (apenas para não-admin)
      if (!hasRole('admin') && !expositor) {
        setError('Expositor não encontrado para este usuário. Entre em contato com o administrador.');
        setLoading(false);
        return;
      }

      // Processar dados para o relatório
      let credenciaisProcessadas = [];
      const expositoresUnicos = new Set();
      const tiposUnicos = new Set();

      data.forEach((credencial, index) => {
        const numeroSequencial = index + 1;
        const dataCredencial = new Date(credencial.created_at).toLocaleDateString('pt-BR');
        const nomeExpositor = credencial.caex_expositores?.nome_empresa || 'Expositor não encontrado';
        const nomeFantasia = credencial.caex_expositores?.nome_fantasia || '';
        const nomeColaborador = credencial.nome;
        const cpf = credencial.cpf;
        const tipoCredencial = credencial.tipo_credencial || 'Não informado';
        const evento = credencial.caex_eventos?.nome || 'Evento não informado';
        
        credenciaisProcessadas.push({
          numeroSequencial,
          credencialId: credencial.id,
          dataCredencial,
          nomeExpositor,
          nomeFantasia,
          nomeColaborador,
          cpf,
          tipoCredencial,
          evento,
          status: credencial.status,
          expositorId: credencial.expositor_id
        });

        // Para estatísticas
        if (credencial.expositor_id) expositoresUnicos.add(credencial.expositor_id);
        if (credencial.tipo_credencial) tiposUnicos.add(credencial.tipo_credencial);
      });

      // Aplicar filtros locais que não podem ser aplicados na query SQL
      if (filters.nomeExpositor) {
        credenciaisProcessadas = credenciaisProcessadas.filter(credencial =>
          credencial.nomeExpositor.toLowerCase().includes(filters.nomeExpositor.toLowerCase())
        );
      }

      if (filters.nomeFantasia) {
        credenciaisProcessadas = credenciaisProcessadas.filter(credencial =>
          credencial.nomeFantasia.toLowerCase().includes(filters.nomeFantasia.toLowerCase())
        );
      }

      if (filters.nomeColaborador) {
        credenciaisProcessadas = credenciaisProcessadas.filter(credencial =>
          credencial.nomeColaborador.toLowerCase().includes(filters.nomeColaborador.toLowerCase())
        );
      }

      setCredenciais(credenciaisProcessadas);
      setStats({
        totalCredenciais: credenciaisProcessadas.length,
        totalExpositores: expositoresUnicos.size,
        totalColaboradores: credenciaisProcessadas.length,
        totalTiposCredencial: tiposUnicos.size
      });

    } catch (err) {
      console.error('Erro ao carregar credenciais:', err);
      setError(err.message || 'Erro ao carregar dados das credenciais');
    } finally {
      setLoading(false);
    }
  };

  // Função para exportar para Excel
  const exportToExcel = () => {
    try {
      if (credenciais.length === 0) {
        alert('Não há dados para exportar.');
        return;
      }

      // Preparar dados para o Excel baseado no perfil do usuário
      const dadosExcel = credenciais.map(credencial => {
        if (hasRole('admin')) {
          // Para administradores: ID / Data / Nome do Expositor / Nome Fantasia / Nome do Colaborador / CPF / Tipo de Credencial
          return {
            'ID': credencial.credencialId,
            'Data': credencial.dataCredencial,
            'Nome do Expositor': credencial.nomeExpositor,
            'Nome Fantasia Expositor': credencial.nomeFantasia,
            'Nome do Colaborador': credencial.nomeColaborador,
            'CPF': credencial.cpf,
            'Função (Tipo de Credencial)': credencial.tipoCredencial
          };
        } else {
          // Para expositores: ID / Data / Nome do Colaborador / CPF / Tipo de Credencial
          return {
            'ID': credencial.credencialId,
            'Data': credencial.dataCredencial,
            'Nome do Colaborador': credencial.nomeColaborador,
            'CPF': credencial.cpf,
            'Função (Tipo de Credencial)': credencial.tipoCredencial
          };
        }
      });

      // Criar workbook
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(dadosExcel);

      // Configurar larguras das colunas
      const colWidths = hasRole('admin') ? [
        { wch: 8 },  // ID
        { wch: 15 }, // Data
        { wch: 30 }, // Nome do Expositor
        { wch: 25 }, // Nome Fantasia Expositor
        { wch: 25 }, // Nome do Colaborador
        { wch: 15 }, // CPF
        { wch: 25 }  // Tipo de Credencial
      ] : [
        { wch: 8 },  // ID
        { wch: 15 }, // Data
        { wch: 25 }, // Nome do Colaborador
        { wch: 15 }, // CPF
        { wch: 25 }  // Tipo de Credencial
      ];
      
      worksheet['!cols'] = colWidths;

      // Adicionar worksheet ao workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Credenciais Expositores');

      // Gerar nome do arquivo com data/hora atual
      const now = new Date();
      const dataHora = now.toISOString().slice(0, 19).replace(/:/g, '-');
      const nomeArquivo = `credenciais_expositores_${dataHora}.xlsx`;

      // Salvar arquivo
      XLSX.writeFile(workbook, nomeArquivo);

      console.log('Excel exportado com sucesso:', nomeArquivo);
    } catch (error) {
      console.error('Erro ao exportar Excel:', error);
      alert('Erro ao exportar para Excel. Tente novamente.');
    }
  };

  // Carregar dados ao montar o componente
  useEffect(() => {
    if (user?.email) {
      fetchExpositores();
      fetchTiposCredencial();
      fetchCredenciais();
    }
  }, [user]);

  // Aplicar filtros
  const handleApplyFilters = () => {
    fetchCredenciais();
  };

  // Limpar filtros
  const handleClearFilters = () => {
    setFilters({
      dataInicio: '',
      dataFim: '',
      expositor: '',
      nomeExpositor: '',
      nomeFantasia: '',
      nomeColaborador: '',
      cpf: '',
      tipoCredencial: ''
    });
    setTimeout(() => {
      fetchCredenciais();
    }, 100);
  };

  // Handler para mudanças nos filtros
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Renderizar estatísticas
  const renderStats = () => (
    <div className="stats-grid">
      <div className="stat-card">
        <div className="stat-icon">
          <FaIdCard />
        </div>
        <div className="stat-content">
          <h3>{stats.totalCredenciais}</h3>
          <p>Total de Credenciais</p>
        </div>
      </div>
      
      <div className="stat-card">
        <div className="stat-icon">
          <FaUsers />
        </div>
        <div className="stat-content">
          <h3>{stats.totalColaboradores}</h3>
          <p>Colaboradores Cadastrados</p>
        </div>
      </div>
      
      <div className="stat-card">
        <div className="stat-icon">
          <FaBuilding />
        </div>
        <div className="stat-content">
          <h3>{stats.totalExpositores}</h3>
          <p>Expositores</p>
        </div>
      </div>
      
      <div className="stat-card">
        <div className="stat-icon">
          <FaTools />
        </div>
        <div className="stat-content">
          <h3>{stats.totalTiposCredencial}</h3>
          <p>Tipos de Credencial</p>
        </div>
      </div>
    </div>
  );

  // Renderizar filtros
  const renderFilters = () => (
    <div className="filters-section">
      <div className="filters-grid">
        {/* Primeira linha - Datas */}
        <div className="filter-group">
          <label>Data Início</label>
          <input
            type="date"
            value={filters.dataInicio}
            onChange={(e) => handleFilterChange('dataInicio', e.target.value)}
            className="filter-input"
          />
        </div>
        
        <div className="filter-group">
          <label>Data Fim</label>
          <input
            type="date"
            value={filters.dataFim}
            onChange={(e) => handleFilterChange('dataFim', e.target.value)}
            className="filter-input"
          />
        </div>

        {/* Expositor (apenas para admin) */}
        {hasRole('admin') && (
          <div className="filter-group">
            <label>Expositor</label>
            <select
              value={filters.expositor}
              onChange={(e) => handleFilterChange('expositor', e.target.value)}
              className="filter-input"
            >
              <option value="">Todos os Expositores</option>
              {expositores.map(expositor => (
                <option key={expositor.id} value={expositor.id}>
                  {expositor.nome_empresa}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="filter-group">
          <label>Nome do Expositor</label>
          <input
            type="text"
            placeholder="Digite o nome do expositor..."
            value={filters.nomeExpositor}
            onChange={(e) => handleFilterChange('nomeExpositor', e.target.value)}
            className="filter-input"
          />
        </div>

        {/* Segunda linha */}
        <div className="filter-group">
          <label>Nome Fantasia Expositor</label>
          <input
            type="text"
            placeholder="Digite o nome fantasia..."
            value={filters.nomeFantasia}
            onChange={(e) => handleFilterChange('nomeFantasia', e.target.value)}
            className="filter-input"
          />
        </div>

        <div className="filter-group">
          <label>Tipo de Credencial</label>
          <select
            value={filters.tipoCredencial}
            onChange={(e) => handleFilterChange('tipoCredencial', e.target.value)}
            className="filter-input"
          >
            <option value="">Todos os Tipos</option>
            {tiposCredencial.map(tipo => (
              <option key={tipo.id} value={tipo.nome}>
                {tipo.nome}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Nome do Colaborador</label>
          <input
            type="text"
            placeholder="Digite o nome do colaborador..."
            value={filters.nomeColaborador}
            onChange={(e) => handleFilterChange('nomeColaborador', e.target.value)}
            className="filter-input"
          />
        </div>

        <div className="filter-group">
          <label>CPF</label>
          <input
            type="text"
            placeholder="Digite o CPF..."
            value={filters.cpf}
            onChange={(e) => handleFilterChange('cpf', e.target.value)}
            className="filter-input"
          />
        </div>
      </div>
      
      <div className="filters-actions">
        <button 
          className="btn-filter apply"
          onClick={handleApplyFilters}
          disabled={loading}
        >
          <FaSearch />
          Aplicar Filtros
        </button>
        
        <button 
          className="btn-filter clear"
          onClick={handleClearFilters}
          disabled={loading}
        >
          Limpar Filtros
        </button>
      </div>
    </div>
  );

  // Renderizar tabela
  const renderTable = () => {
    if (loading) {
      return (
        <div className="loading-container">
          <FaSpinner className="loading-spinner" />
          <p>Carregando dados das credenciais...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="error-container">
          <FaInfoCircle className="error-icon" />
          <h3>Erro ao carregar dados</h3>
          <p>{error}</p>
          {error.includes('Expositor não encontrado') ? (
            <div className="error-details">
              <p><strong>Email do usuário:</strong> {user?.email}</p>
              <p>Verifique se existe um expositor cadastrado com este email e se está ativo no sistema.</p>
            </div>
          ) : (
            <button className="btn-retry" onClick={fetchCredenciais}>
              Tentar Novamente
            </button>
          )}
        </div>
      );
    }

    if (credenciais.length === 0) {
      return (
        <div className="empty-container">
          <FaIdCard className="empty-icon" />
          <h3>Nenhuma credencial encontrada</h3>
          <p>Não há credenciais que correspondam aos filtros aplicados.</p>
        </div>
      );
    }

    // Colunas condicionais baseadas no perfil do usuário
    const renderTableHeaders = () => {
      if (hasRole('admin')) {
        return (
          <tr>
            <th>ID</th>
            <th>Data</th>
            <th>Nome do Expositor</th>
            <th>Nome Fantasia Expositor</th>
            <th>Nome do Colaborador</th>
            <th>CPF</th>
            <th>Função (Tipo de Credencial)</th>
          </tr>
        );
      } else {
        return (
          <tr>
            <th>ID</th>
            <th>Data</th>
            <th>Nome do Colaborador</th>
            <th>CPF</th>
            <th>Função (Tipo de Credencial)</th>
          </tr>
        );
      }
    };

    const renderTableRows = () => {
      return credenciais.map((credencial) => {
        if (hasRole('admin')) {
          return (
            <tr key={credencial.credencialId}>
              <td>{credencial.credencialId}</td>
              <td>{credencial.dataCredencial}</td>
              <td>{credencial.nomeExpositor}</td>
              <td>{credencial.nomeFantasia}</td>
              <td>{credencial.nomeColaborador}</td>
              <td>{credencial.cpf}</td>
              <td>{credencial.tipoCredencial}</td>
            </tr>
          );
        } else {
          return (
            <tr key={credencial.credencialId}>
              <td>{credencial.credencialId}</td>
              <td>{credencial.dataCredencial}</td>
              <td>{credencial.nomeColaborador}</td>
              <td>{credencial.cpf}</td>
              <td>{credencial.tipoCredencial}</td>
            </tr>
          );
        }
      });
    };

    return (
      <div className="table-container">
        <table className="report-table">
          <thead>
            {renderTableHeaders()}
          </thead>
          <tbody>
            {renderTableRows()}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="relatorio-container">
      {/* Header com estatísticas */}
      <div className="relatorio-header">
        {/* Informação do expositor se não for admin */}
        {!hasRole('admin') && expositorLogado && (
          <div className="expositor-info">
            <h4>Relatório de: {expositorLogado.nome_empresa}</h4>
            {expositorLogado.nome_fantasia && (
              <h5>Nome Fantasia: {expositorLogado.nome_fantasia}</h5>
            )}
            <p>CNPJ: {expositorLogado.cnpj}</p>
          </div>
        )}
        
        <div className="header-controls">
          <button 
            className={`btn-toggle ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FaFilter />
            {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
          </button>
          
          <div className="export-buttons">
            <button 
              className="btn-export excel" 
              onClick={exportToExcel}
              disabled={loading || credenciais.length === 0}
            >
              <FaFileExcel />
              Excel
            </button>
          </div>
        </div>

        {renderStats()}
      </div>

      {/* Filtros */}
      {showFilters && renderFilters()}

      {/* Tabela de dados */}
      <div className="relatorio-content">
        {renderTable()}
      </div>
    </div>
  );
};

export default RelatorioCredenciais;